import { Modal, Button } from 'react-bootstrap';
import ApiService from '../../services/ApiService';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

/**
 * AllAppointmentsCancelModal Component
 * A reusable modal for requesting appointment cancellation based on different scenarios.
 *
 * Props:
 * - show: Boolean to control the modal visibility
 * - onHide: Function to close the modal
 * - module: String indicating the scenario ('days', 'time', 'outofoffice')
 * - bookedAppointments: Object containing details of the booked appointment
 * - onYes: Function to handle the cancellation logic
 * - onNo: Function to handle the "No" action (close without action)
 */
const AllAppointmentsCancelModal = ({ show, onHide, module, bookedAppointments, onNo, indexValue, hospitalInfo, setHospitalInfo, valueToRemove, physicianData, setData, rowIndexValue }) => {
  const { t } = useTranslation();
  const role = Number(localStorage.getItem('role'));
  // Determine the dynamic message based on the `module` value
  const getMessage = () => {
    if (module === 'days' || module === 'time') {
      // Visiting days or visiting hours
      return t('modal.cancelAllAppointmentWork');
    } else {
      // Out of office
      return t('modal.cancelAllAppointmentDay');
    }
  };

  const removeFromArray = (array, valueToRemove) => {
		if (typeof valueToRemove === 'string') {
		  return array.filter(item => item !== valueToRemove); // For strings
		} else if (typeof valueToRemove === 'object' && valueToRemove !== null && 'id' in valueToRemove) {
		  return array.filter(item => item.id !== valueToRemove.id); // For objects with `id`
		}
		return array;
	};	
  
  // Handle the "No" button click
  const handleNo = async () => {
    if (module === 'out_of_office_hospital') {
      // Ensure indexValue and rowIndexValue are valid
      if (hospitalInfo && indexValue >= 0 && indexValue < hospitalInfo.length) {
        const hospitalData = { ...hospitalInfo[indexValue] }; // Create a copy of the object to avoid direct mutation
        // Check and update the out_of_office array
        if (hospitalData.out_of_office && rowIndexValue >= 0 && rowIndexValue < hospitalData.out_of_office.length) {
          hospitalData.out_of_office = hospitalData.out_of_office.filter((_, idx) => idx !== rowIndexValue);
          // If out_of_office becomes empty, set it to ['["",""]']
          if (hospitalData.out_of_office.length === 0) {
            hospitalData.out_of_office = ['["",""]'];
          }
          // Update the hospitalInfo array
          const updatedHospitalInfo = [...hospitalInfo];
          updatedHospitalInfo[indexValue] = hospitalData;
          setHospitalInfo(updatedHospitalInfo); // Commit the changes
          return; // Exit to ensure existing logic is not executed
        }
      }
    }
    // Ensure physicianData and out_of_office exist and indexValue is within bounds
    if (physicianData && physicianData.out_of_office && indexValue >= 0 && indexValue < physicianData.out_of_office.length) {
      // Remove the entry at indexValue from out_of_office
      const updatedPhysicianData = { ...physicianData }; // Create a copy to avoid direct mutation
      updatedPhysicianData.out_of_office = updatedPhysicianData.out_of_office.filter((_, idx) => idx !== indexValue);

      // If out_of_office becomes empty, set it to ['["",""]']
      if (updatedPhysicianData.out_of_office.length === 0) {
        updatedPhysicianData.out_of_office = ['["",""]'];
      }

      // Update the physician data (e.g., via setData)
      setData(updatedPhysicianData);
    }
  };
  const handleNoClick = () => {
    if (module === 'out_of_office' || module === 'out_of_office_hospital') {
      handleNo(); // Call handleNo to update the physician data
    }
    onNo(); // Call onNo (callback passed in as a prop)
  };

// Handle the "Yes" button click
const handleYes = async () => {
  try {
    // Create cancellation promises
    const cancellationPromises = bookedAppointments.map((appointment) =>
      ApiService.deleteAppointment(appointment.id, { cancelled_reason: 'Physician Unavailable' }) // Pass the reason
    );

    // Wait for all cancellation requests to complete
    await Promise.all(cancellationPromises);

    // Show success toast notification
    toast.success(t('toast.cancelAllAppointment'));

    if(module === 'days' || module === 'days_hospital' || module === 'day_hospital'){
        // Remove the relevant day or object from the state
        const updatedDays = removeFromArray(hospitalInfo[indexValue].days, valueToRemove); // Adjust based on structure
        const updatedHospitalInfo = hospitalInfo.map((item, idx) => {
          if (idx === indexValue) {
            return {
              ...item,
              days: updatedDays, // Update the `days` property
            };
          }
          return item;
        });
        if (module === 'day_hospital' && rowIndexValue !== null) {
          const updatedVisitingSchedules = updatedHospitalInfo[indexValue].visitingSchedules.map((schedule, idx) => {
            if (idx === rowIndexValue) {
              return {
                ...schedule,
                days: removeFromArray(schedule.days, valueToRemove), // Update the `days` property of the specific schedule
              };
            }
            return schedule;
          });
          // Update the visitingSchedules property for the specific indexValue
          updatedHospitalInfo[indexValue] = {
            ...updatedHospitalInfo[indexValue],
            visitingSchedules: updatedVisitingSchedules,
          };
        }
        if (module === 'days_hospital' && rowIndexValue !== null) {
          // Remove the specific visitingSchedule entry at rowIndexValue
          const updatedVisitingSchedules = updatedHospitalInfo[indexValue].visitingSchedules.filter((_, idx) => idx !== rowIndexValue);
        
          // Update the visitingSchedules property for the specific indexValue
          updatedHospitalInfo[indexValue] = {
            ...updatedHospitalInfo[indexValue],
            visitingSchedules: updatedVisitingSchedules,
          };
        }        
      setHospitalInfo(updatedHospitalInfo); 
    } else if (module === 'time' || module === 'time_hospital') {
      // Create a new hospitalInfo array with updated startTime and endTime for the specific index
      let updatedHospitalInfo = hospitalInfo.map((item, idx) => {
        if (idx === indexValue) {
          return {
            ...item,
            startTime: '', // Remove startTime by setting it to an empty string or null
            endTime: '',   // Remove endTime by setting it to an empty string or null
          };
        }
        return item;
      });
      if (rowIndexValue !== null && module === 'time_hospital') {
        // Update visitingSchedules[rowIndexValue].startTime and endTime
        const updatedVisitingSchedules = updatedHospitalInfo[indexValue].visitingSchedules.map((schedule, idx) => {
          if (idx === rowIndexValue) {
            return {
              ...schedule,
              startTime: '', // Remove startTime by setting it to an empty string
              endTime: '',   // Remove endTime by setting it to an empty string
            };
          }
          return schedule;
        });
        // Update the visitingSchedules property for the specific indexValue
        updatedHospitalInfo[indexValue] = {
          ...updatedHospitalInfo[indexValue],
          visitingSchedules: updatedVisitingSchedules,
        };
      }
      // Update the hospitalInfo state
      setHospitalInfo(updatedHospitalInfo);
    } else if (module === 'schedule') {
      let updatedHospitalInfo = removeFromArray(hospitalInfo, valueToRemove); // Adjust for your structure
        // Check if updatedHospitalInfo is empty and add a default object if necessary
        if (updatedHospitalInfo.length === 0) {
          updatedHospitalInfo = [{ days: [], startTime: '', endTime: '' }];
        }
      setHospitalInfo(updatedHospitalInfo); // Update the state with the modified hospitalInfo
    } else { //out_of_office
      // Check if 'physicianData' and 'newOutOfOffice' exist and the indexValue is within bounds
      if (physicianData && physicianData.newOutOfOffice && indexValue >= 0 && indexValue < physicianData.newOutOfOffice.length) {
        // Replace the out_of_office value at the given indexValue with newOutOfOffice value
        let updatedPhysicianData = { ...physicianData }; // Create a copy to avoid direct mutation
        updatedPhysicianData.out_of_office[indexValue] = physicianData.newOutOfOffice[indexValue];

        // Optionally, update the state or call a function to handle the new data
        setData(updatedPhysicianData);
      }

      if (rowIndexValue !== null && module === 'out_of_office_hospital') {
        // Handle the logic for hospitalInfo[indexValue] when rowIndexValue is not null
        if (
          hospitalInfo &&
          hospitalInfo[indexValue] &&
          hospitalInfo[indexValue].newOutOfOffice &&
          indexValue >= 0 &&
          indexValue < hospitalInfo.length
        ) {
          // Create a copy of the hospitalInfo array to avoid direct mutation
          const updatedHospitalInfo = [...hospitalInfo];
    
          // Replace the out_of_office value at the given indexValue and rowIndexValue
          updatedHospitalInfo[indexValue] = {
            ...updatedHospitalInfo[indexValue],
            out_of_office: updatedHospitalInfo[indexValue].out_of_office.map((item, idx) => {
              // If we are at the correct rowIndexValue, replace the out_of_office value
              if (idx === rowIndexValue) {
                return hospitalInfo[indexValue].newOutOfOffice[0]; // Replace with the value from newOutOfOffice
              }
              return item; // Keep the other out_of_office values unchanged
            }),
          };          
          // Update the hospitalInfo state
          setHospitalInfo(updatedHospitalInfo);
        }
    }    
    }
    onHide();
  } catch (error) {
    // Handle error
    const errorMessage = error?.response?.data?.message || t('toast.appointmentCancel');
    toast.error(errorMessage);
  }
};

  return (
    <Modal show={show} onHide={onHide} backdrop="static">
      <Modal.Header closeButton={false} className={`${role === 4 ? 'admin-modal-header' : ''}`}>
        <Modal.Title>{t('modal.cancelAppointment')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{getMessage()}</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" className={`${role === 4 ? 'btn-tmAquaDgreen' : ''}`} onClick={handleNoClick}>
          {t('modal.no')}
        </Button>
        <Button variant="primary" className={`${role === 4 ? 'btn-tmAquaGreen' : ''}`} onClick={handleYes}>
          {t('modal.yes')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AllAppointmentsCancelModal;
